/**
 * One tokenizer for every inline formatter in the app (F3).
 *
 * Alternation order is load-bearing: `code` precedes the emphasis forms so
 * `` `**not bold**` `` stays literal, and `bold` precedes `italic` so `**x**`
 * is not read as an empty italic wrapping `*x*`.
 *
 * Emphasis requires a non-space character adjacent to each delimiter, which is
 * the CommonMark rule. Without it `5 * 3` pairs its lone asterisk with the next
 * one on the line and italicises everything between them. Underscore emphasis
 * additionally requires non-word characters outside the delimiters, so
 * `snake_case_identifier` is left alone.
 */
const PATTERNS = [
  String.raw`(?<link>\[(?<linkLabel>[^\]\n]+)\]\((?<linkHref>[^)\s]+)\))`,
  String.raw`(?<code>\`(?<codeText>[^\`\n]+)\`)`,
  String.raw`(?<bold>\*\*(?<boldText>[^\s*](?:[^*]*[^\s*])?)\*\*)`,
  String.raw`(?<strike>~~(?<strikeText>[^\s~](?:[^~]*[^\s~])?)~~)`,
  String.raw`(?<italic>\*(?<italicText>[^\s*](?:[^*\n]*[^\s*])?)\*)`,
  String.raw`(?<uitalic>(?<![\w])_(?<uitalicText>[^\s_](?:[^_\n]*[^\s_])?)_(?![\w]))`,
]

const INLINE_RE = new RegExp(PATTERNS.join('|'), 'g')

/**
 * @param {string} value
 * @returns {Array<{ kind: string, text: string, href?: string }>}
 */
export function tokenizeInline(value) {
  const source = String(value ?? '')
  const tokens = []
  let lastIndex = 0

  INLINE_RE.lastIndex = 0
  let match = INLINE_RE.exec(source)
  while (match !== null) {
    if (match.index > lastIndex) {
      tokens.push({ kind: 'plain', text: source.slice(lastIndex, match.index) })
    }
    const groups = match.groups ?? {}
    if (groups.link !== undefined) {
      tokens.push({ kind: 'link', text: groups.linkLabel, href: groups.linkHref })
    } else if (groups.code !== undefined) tokens.push({ kind: 'code', text: groups.codeText })
    else if (groups.bold !== undefined) tokens.push({ kind: 'bold', text: groups.boldText })
    else if (groups.strike !== undefined) tokens.push({ kind: 'strike', text: groups.strikeText })
    else if (groups.italic !== undefined) tokens.push({ kind: 'italic', text: groups.italicText })
    else tokens.push({ kind: 'italic', text: groups.uitalicText })

    lastIndex = match.index + match[0].length
    match = INLINE_RE.exec(source)
  }

  if (lastIndex < source.length) tokens.push({ kind: 'plain', text: source.slice(lastIndex) })
  return tokens
}
